// src/components/site-editor/panels/ServicesPanel.js
import React, { useState, useRef } from 'react';
import { Plus, Edit2, Trash2, DollarSign, ChevronUp, ChevronDown, X, Sparkles, Upload, Loader2, AlertCircle } from 'lucide-react';
import { useWebsiteBuilder } from '../../../contexts/WebsiteBuilderContext';
import { sanitizeHtml } from '../../../utils/sanitize';
import useUserTier from '../../../hooks/useUserTier';
import InlineCallout from '../InlineCallout';
import ServiceLimitModal from '../ServiceLimitModal';
import Toggle from '../../Toggle';
import SimpleRichEditor from '../../common/SimpleRichEditor';
import ToggleGroup from '../../common/ToggleGroup';
import { uploadSiteImage } from '../../../services/image-upload-service';
import { deleteFile } from '../../../services/storage-service';

// Slug generation utility
function generateSlug(name, existingServices = [], currentServiceId = null) {
  // Generate base slug from name
  const baseSlug = name
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')  // Remove special characters
    .replace(/\s+/g, '-')      // Spaces to hyphens
    .replace(/-+/g, '-')       // Collapse multiple hyphens
    .substring(0, 60);         // Max length

  // Check for duplicates (excluding current service if editing)
  const existingSlugs = existingServices
    .filter(s => s.id !== currentServiceId)
    .map(s => s.slug)
    .filter(Boolean);

  // If no duplicates, return base slug
  if (!existingSlugs.includes(baseSlug)) {
    return baseSlug;
  }

  // Find available slug by appending numbers
  let counter = 2;
  let slug = `${baseSlug}-${counter}`;
  while (existingSlugs.includes(slug)) {
    counter++;
    slug = `${baseSlug}-${counter}`;
  }

  return slug;
}

// Comprehensive keyword mappings for AI pricing
const serviceKeywords = {
  divination: {
    keywords: ['tarot', 'oracle', 'cards', 'card reading', 'card pull', 'runes', 'pendulum', 'i ching', 'tea leaves', 'scrying', 'divination'],
    multiplier: 1.0,
    category: 'Divination',
  },
  astrology: {
    keywords: ['astrology', 'natal', 'birth chart', 'chart reading', 'horoscope', 'transit', 'synastry', 'composite', 'solar return', 'lunar return', 'progressed', 'zodiac', 'planetary'],
    multiplier: 1.15,
    category: 'Astrology',
  },
  numerology: {
    keywords: ['numerology', 'life path', 'soul number', 'destiny number', 'expression number', 'name analysis', 'number reading'],
    multiplier: 1.1,
    category: 'Numerology',
  },
  mediumship: {
    keywords: ['medium', 'mediumship', 'spirit', 'spirits', 'deceased', 'loved ones', 'crossed over', 'channeling', 'channel', 'spirit guide', 'guides', 'messages from', 'connect with deceased', 'grief', 'afterlife'],
    multiplier: 1.25,
    category: 'Mediumship',
  },
  psychic: {
    keywords: ['psychic', 'clairvoyant', 'clairaudient', 'clairsentient', 'claircognizant', 'intuitive reading', 'psychic reading', 'remote viewing', 'precognition'],
    multiplier: 1.15,
    category: 'Psychic Reading',
  },
  energyHealing: {
    keywords: ['reiki', 'energy healing', 'energy work', 'chakra', 'chakras', 'chakra balancing', 'aura', 'aura cleansing', 'aura reading', 'pranic', 'pranic healing', 'quantum healing', 'biofield', 'laying of hands', 'distance healing', 'remote healing'],
    multiplier: 1.0,
    category: 'Energy Healing',
  },
  hypnotherapy: {
    keywords: ['hypnotherapy', 'hypnosis', 'past life', 'past-life', 'regression', 'life between lives', 'lbl', 'qhht', 'quantum healing hypnosis', 'dolores cannon', 'subconscious', 'trance', 'trance work', 'age regression', 'timeline'],
    multiplier: 1.3,
    category: 'Hypnotherapy',
  },
  akashic: {
    keywords: ['akashic', 'akashic records', 'soul records', 'book of life', 'akashic reading'],
    multiplier: 1.25,
    category: 'Akashic Records',
  },
  coaching: {
    keywords: ['coaching', 'spiritual coaching', 'guidance', 'mentoring', 'spiritual guidance', 'soul coaching', 'transformation', 'breakthrough', 'clarity session', 'discovery call'],
    multiplier: 1.1,
    category: 'Spiritual Coaching',
  },
  breathwork: {
    keywords: ['breathwork', 'breath work', 'breathing', 'breath session', 'holotropic', 'rebirthing', 'pranayama', 'breath journey'],
    multiplier: 1.0,
    category: 'Breathwork',
  },
  meditation: {
    keywords: ['meditation', 'guided meditation', 'mindfulness', 'visualization', 'guided journey', 'shamanic journey', 'journey work'],
    multiplier: 0.95,
    category: 'Meditation',
  },
  soundHealing: {
    keywords: ['sound healing', 'sound bath', 'singing bowl', 'singing bowls', 'crystal bowls', 'tuning fork', 'tuning forks', 'gong', 'sound therapy', 'vibrational healing', 'frequency healing'],
    multiplier: 1.0,
    category: 'Sound Healing',
  },
  shamanic: {
    keywords: ['shamanic', 'shaman', 'soul retrieval', 'power animal', 'spirit animal', 'extraction', 'shamanic healing', 'journeying', 'drum journey', 'plant medicine', 'ceremony'],
    multiplier: 1.2,
    category: 'Shamanic Work',
  },
  intuitive: {
    keywords: ['intuitive reading', 'intuitive session', 'intuitive consultation', 'intuitive guidance', 'spiritual reading'],
    multiplier: 1.0,
    category: 'Intuitive Reading',
  },
  clearing: {
    keywords: ['clearing', 'cleansing', 'space clearing', 'house clearing', 'entity', 'attachment', 'cord cutting', 'energy clearing', 'spiritual cleansing', 'banishing', 'protection'],
    multiplier: 1.15,
    category: 'Energy Clearing',
  },
  ritual: {
    keywords: ['spell', 'ritual', 'magic', 'magick', 'manifestation', 'intention setting', 'candle magic', 'moon ritual', 'full moon', 'new moon', 'sabbat'],
    multiplier: 1.15,
    category: 'Ritual Work',
  },
  angel: {
    keywords: ['angel', 'angels', 'archangel', 'angelic', 'angel reading', 'angel messages', 'angel guidance'],
    multiplier: 1.05,
    category: 'Angel Reading',
  },
  quick: {
    keywords: ['quick', 'mini', 'express', 'flash', 'speed', 'brief', 'short'],
    multiplier: 0.85,
    category: null,
  },
  comprehensive: {
    keywords: ['deep dive', 'deep-dive', 'comprehensive', 'extensive', 'in-depth', 'in depth', 'full', 'complete', 'detailed', 'thorough', 'intensive', 'extended', 'deluxe', 'premium', 'vip'],
    multiplier: 1.25,
    category: null,
  },
};

const basePricesByDuration = {
  15: { low: 25, mid: 40, high: 60 },
  30: { low: 45, mid: 70, high: 95 },
  45: { low: 65, mid: 95, high: 130 },
  60: { low: 85, mid: 120, high: 165 },
  75: { low: 100, mid: 140, high: 190 },
  90: { low: 115, mid: 160, high: 220 },
  120: { low: 140, mid: 200, high: 280 },
  150: { low: 170, mid: 240, high: 340 },
  180: { low: 200, mid: 280, high: 400 },
};

const analyzeServiceForPricing = (serviceName, durationMinutes) => {
  const nameLower = (serviceName || '').toLowerCase();
  let categoryMultiplier = 1.0;
  let modifierMultiplier = 1.0;
  let detectedCategory = 'General Reading';
  let detectedModifiers = [];

  for (const [, data] of Object.entries(serviceKeywords)) {
    let matched = false;
    for (const keyword of data.keywords) {
      if (nameLower.includes(keyword)) {
        if (data.category) {
          if (data.multiplier > categoryMultiplier) {
            categoryMultiplier = data.multiplier;
            detectedCategory = data.category;
          }
        } else {
          modifierMultiplier *= data.multiplier;
          detectedModifiers.push(keyword);
        }
        matched = true;
        break;
      }
    }
    if (matched && data.category) continue;
  }

  const durations = Object.keys(basePricesByDuration).map(Number).sort((a, b) => a - b);
  let basePrices;

  if (basePricesByDuration[durationMinutes]) {
    basePrices = basePricesByDuration[durationMinutes];
  } else {
    const lowerDuration = durations.filter(d => d <= durationMinutes).pop() || durations[0];
    const upperDuration = durations.filter(d => d >= durationMinutes).shift() || durations[durations.length - 1];

    if (lowerDuration === upperDuration) {
      basePrices = basePricesByDuration[lowerDuration];
    } else {
      const ratio = (durationMinutes - lowerDuration) / (upperDuration - lowerDuration);
      const lower = basePricesByDuration[lowerDuration];
      const upper = basePricesByDuration[upperDuration];
      basePrices = {
        low: Math.round(lower.low + (upper.low - lower.low) * ratio),
        mid: Math.round(lower.mid + (upper.mid - lower.mid) * ratio),
        high: Math.round(lower.high + (upper.high - lower.high) * ratio),
      };
    }
  }

  const totalMultiplier = categoryMultiplier * modifierMultiplier;
  const adjustedPrices = {
    low: Math.round(basePrices.low * totalMultiplier / 5) * 5,
    mid: Math.round(basePrices.mid * totalMultiplier / 5) * 5,
    high: Math.round(basePrices.high * totalMultiplier / 5) * 5,
  };

  return {
    prices: adjustedPrices,
    category: detectedCategory,
    modifiers: detectedModifiers,
    multiplier: totalMultiplier,
  };
};

const ServicesPanel = ({ setCustomBackHandler }) => {
  const { state, actions } = useWebsiteBuilder();
  const userTier = useUserTier();
  const services = state.draft.content.services || [];

  const [editingService, setEditingService] = useState(null); // null | 'new' | serviceId
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // Tier limits
  const tierLimits = {
    free: 1,
    pro: 3,
    elite: Infinity
  };

  const serviceLimit = tierLimits[userTier] || tierLimits.pro;
  const canAddService = services.length < serviceLimit;

  // Get counter text for header
  const getCounterText = () => {
    if (userTier === 'elite') {
      return `${services.length} service${services.length !== 1 ? 's' : ''}`;
    }
    return `${services.length} of ${serviceLimit} service${serviceLimit !== 1 ? 's' : ''}`;
  };

  // Override back button behavior when editing a service
  React.useEffect(() => {
    if (setCustomBackHandler) {
      if (editingService !== null) {
        // When editing, Back button returns to services list
        setCustomBackHandler(() => () => setEditingService(null));
      } else {
        // When not editing, use default behavior
        setCustomBackHandler(null);
      }
    }
    return () => {
      if (setCustomBackHandler) {
        setCustomBackHandler(null);
      }
    };
  }, [editingService, setCustomBackHandler]);

  const handleAddService = () => {
    if (!canAddService) {
      setShowLimitModal(true);
      return;
    }
    setEditingService('new');
  };

  const handleEditService = (serviceId) => {
    setEditingService(serviceId);
  };

  const handleDeleteService = (serviceId) => {
    const updatedServices = services.filter(s => s.id !== serviceId);
    actions.updateContent('services', updatedServices);
    setDeleteConfirm(null);
  };

  const handleToggleActive = (serviceId) => {
    const updatedServices = services.map(s =>
      s.id === serviceId ? { ...s, active: !s.active } : s
    );
    actions.updateContent('services', updatedServices);
  };

  const handleReorder = (serviceId, direction) => {
    const currentIndex = services.findIndex(s => s.id === serviceId);
    if (
      (direction === 'up' && currentIndex === 0) ||
      (direction === 'down' && currentIndex === services.length - 1)
    ) {
      return;
    }

    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    const reordered = [...services];
    const [moved] = reordered.splice(currentIndex, 1);
    reordered.splice(newIndex, 0, moved);
    actions.updateContent('services', reordered);
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-lg font-semibold text-gray-900">Your Offerings</h3>
          {services.length > 0 && (
            <span className="text-xs font-medium text-gray-500 bg-gray-100 px-2 py-1 rounded">
              {getCounterText()}
            </span>
          )}
        </div>
        <p className="text-sm text-gray-600">
          Add the services you offer—readings, healings, sessions. Include pricing, duration, and what clients can expect.
        </p>
      </div>

      {/* Inline Callout */}
      <InlineCallout
        text="Feeling uncertain about pricing? You're not alone — most practitioners do at first."
        playbookTopic="earning-and-pricing"
      />

      {/* Service List */}
      {services.length > 0 && editingService === null && (
        <div className="space-y-3">
          {services.map((service, index) => (
            <ServiceCard
              key={service.id}
              service={service}
              index={index}
              totalServices={services.length}
              onEdit={() => handleEditService(service.id)}
              onDelete={() => setDeleteConfirm(service.id)}
              onToggleActive={() => handleToggleActive(service.id)}
              onMoveUp={() => handleReorder(service.id, 'up')}
              onMoveDown={() => handleReorder(service.id, 'down')}
            />
          ))}
        </div>
      )}

      {/* Service Form */}
      {editingService !== null && (
        <ServiceForm
          service={editingService === 'new' ? null : services.find(s => s.id === editingService)}
          isNew={editingService === 'new'}
          onSave={(serviceData) => {
            if (editingService === 'new') {
              // Generate slug for new service
              const slug = generateSlug(serviceData.name, services);

              // Add new service
              const newService = {
                id: `service_${Date.now()}`,
                ...serviceData,
                slug,
                createdAt: new Date().toISOString()
              };
              actions.updateContent('services', [...services, newService]);
            } else {
              // Check if name changed and regenerate slug if needed
              const currentService = services.find(s => s.id === editingService);
              const slug = serviceData.name !== currentService?.name
                ? generateSlug(serviceData.name, services, editingService)
                : currentService.slug || generateSlug(serviceData.name, services, editingService);

              // Update existing service
              const updatedServices = services.map(s =>
                s.id === editingService ? { ...s, ...serviceData, slug } : s
              );
              actions.updateContent('services', updatedServices);
            }
            setEditingService(null);
          }}
          onCancel={() => setEditingService(null)}
        />
      )}

      {/* Empty State */}
      {services.length === 0 && editingService === null && (
        <div className="text-center py-8">
          <DollarSign className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-sm text-gray-600 mb-6">
            Add your first service to start accepting bookings
          </p>
        </div>
      )}

      {/* Add Service Button */}
      {editingService === null && (
        <button
          onClick={handleAddService}
          className="w-full flex items-center justify-center gap-2 px-4 py-2.5 border border-[#7c3aed] rounded-lg text-sm font-medium text-[#7c3aed] hover:bg-[#F5F3FF] hover:border-[#6d28d9] transition-all duration-150"
          style={{ fontSize: '14px', fontWeight: 500 }}
        >
          <Plus className="w-4 h-4" />
          Add Service
        </button>
      )}

      {/* Service Limit Modal */}
      <ServiceLimitModal
        isOpen={showLimitModal}
        onClose={() => setShowLimitModal(false)}
        tier={userTier}
        currentCount={services.length}
      />

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <DeleteConfirmModal
          serviceName={services.find(s => s.id === deleteConfirm)?.name}
          onConfirm={() => handleDeleteService(deleteConfirm)}
          onCancel={() => setDeleteConfirm(null)}
        />
      )}
    </div>
  );
};

// Service Card Component
const ServiceCard = ({ service, index, totalServices, onEdit, onDelete, onToggleActive, onMoveUp, onMoveDown }) => {
  const showReorderButtons = totalServices > 1;

  const getServiceTypeDisplay = () => {
    if (service.serviceType === 'in-person') return 'In-Person';
    if (service.serviceType === 'phone') return 'Phone';
    if (service.serviceType === 'hybrid') return 'Hybrid';
    return 'Virtual';
  };

  return (
    <div className="border border-gray-200 rounded-lg p-4 hover:border-purple-200 transition-colors">
      <div className="flex items-start gap-3">
        {/* Reorder buttons */}
        {showReorderButtons && (
          <div className="flex flex-col gap-0.5 pt-1">
            <button
              onClick={onMoveUp}
              disabled={index === 0}
              className={`p-0.5 rounded transition-colors ${
                index === 0
                  ? 'text-gray-300 cursor-not-allowed'
                  : 'text-gray-400 hover:text-purple-600 hover:bg-purple-50'
              }`}
            >
              <ChevronUp className="w-4 h-4" />
            </button>
            <button
              onClick={onMoveDown}
              disabled={index === totalServices - 1}
              className={`p-0.5 rounded transition-colors ${
                index === totalServices - 1
                  ? 'text-gray-300 cursor-not-allowed'
                  : 'text-gray-400 hover:text-purple-600 hover:bg-purple-50'
              }`}
            >
              <ChevronDown className="w-4 h-4" />
            </button>
          </div>
        )}

        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-semibold text-gray-900 mb-1">
            {service.name}
          </h4>
          <div
            className="text-xs text-gray-600 mb-2 line-clamp-2"
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(service.description) }}
          />
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <span>{service.duration} min</span>
            <span>•</span>
            <span className="font-medium text-gray-900">${service.price}</span>
            <span>•</span>
            <span>{getServiceTypeDisplay()}</span>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Toggle
            enabled={service.active}
            onChange={onToggleActive}
          />
          <button
            onClick={onEdit}
            className="p-1.5 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded transition-colors"
          >
            <Edit2 className="w-4 h-4" />
          </button>
          <button
            onClick={onDelete}
            className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

// Service Form Component
const ServiceForm = ({ service, isNew, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    name: service?.name || '',
    tagline: service?.tagline || '',
    description: service?.description || '',
    duration: service?.duration ? String(service.duration) : '60',
    customDuration: '',
    price: service?.price !== undefined ? String(service.price) : '',
    serviceType: service?.serviceType || 'virtual',
    location: service?.location || '',
    showLocationOnWebsite: service?.showLocationOnWebsite ?? false,
    active: service?.active ?? true,
    serviceImage: service?.serviceImage || '',
    serviceImagePath: service?.serviceImagePath || '',
    whatToExpect: service?.whatToExpect || '',
    whoIsThisFor: service?.whoIsThisFor || ''
  });

  const [errors, setErrors] = useState({});
  const [showPricingHelper, setShowPricingHelper] = useState(false);
  const [showAdditionalDetails, setShowAdditionalDetails] = useState(
    !!(service?.whatToExpect || service?.whoIsThisFor)
  );
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const fileInputRef = useRef(null);

  const durationOptions = [
    { value: '15', label: '15 min' },
    { value: '30', label: '30 min' },
    { value: '45', label: '45 min' },
    { value: '60', label: '60 min' },
    { value: '90', label: '90 min' },
    { value: '120', label: '2 hours' },
    { value: 'custom', label: 'Custom' }
  ];

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    if (errors[field]) {
      setErrors({ ...errors, [field]: null });
    }
  };

  // Get effective duration for pricing
  const getEffectiveDuration = () => {
    if (formData.duration === 'custom') {
      const cd = Number(formData.customDuration);
      return cd > 0 ? cd : null;
    }
    return Number(formData.duration) || null;
  };

  const effectiveDuration = getEffectiveDuration();
  const pricingAnalysis = effectiveDuration
    ? analyzeServiceForPricing(formData.name, effectiveDuration)
    : null;

  // Image upload handler
  const compressAndConvertToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      img.onload = () => {
        // Compress to 800px max width for service images
        const maxSize = 800;
        let width = img.width;
        let height = img.height;

        if (width > height) {
          if (width > maxSize) {
            height = (height * maxSize) / width;
            width = maxSize;
          }
        } else {
          if (height > maxSize) {
            width = (width * maxSize) / height;
            height = maxSize;
          }
        }

        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(img, 0, 0, width, height);

        const base64 = canvas.toDataURL('image/jpeg', 0.8);
        resolve(base64);
      };

      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = URL.createObjectURL(file);
    });
  };

  const handleImageSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setUploadError('Please upload an image file (JPG, PNG, or WEBP)');
      return;
    }

    // Validate file size (2MB limit)
    if (file.size > 2 * 1024 * 1024) {
      setUploadError('Image must be less than 2MB');
      return;
    }

    setUploadError(null);
    setIsUploadingImage(true);

    try {
      // Try Firebase Storage first (with Base64 fallback built-in)
      try {
        const authService = (await import('../../../services/auth-service')).default;
        const currentUser = authService.getCurrentUser();

        if (currentUser?.uid && uploadSiteImage) {
          const result = await uploadSiteImage(file, 'service', currentUser.uid);
          if (result.success && result.url) {
            setFormData(prev => ({
              ...prev,
              serviceImage: result.url,
              serviceImagePath: result.fullPath || null
            }));
            setIsUploadingImage(false);
            if (fileInputRef.current) {
              fileInputRef.current.value = '';
            }
            return;
          }
        }
      } catch (fbError) {

      }

      // Fallback to base64
      const base64Url = await compressAndConvertToBase64(file);
      handleChange('serviceImage', base64Url);

    } catch (error) {
      setUploadError(error.message || 'Failed to upload image');
    } finally {
      setIsUploadingImage(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemoveImage = () => {
    // Delete from Firebase Storage if we have a path
    if (formData.serviceImagePath) {
      deleteFile(formData.serviceImagePath).catch(err =>
        console.warn('⚠️ Failed to delete old image from Storage:', err)
      );
    }
    setFormData(prev => ({ ...prev, serviceImage: '', serviceImagePath: '' }));
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Service name is required';
    }

    if (!formData.tagline.trim()) {
      newErrors.tagline = 'Tagline is required';
    } else if (formData.tagline.length > 120) {
      newErrors.tagline = 'Tagline must be 120 characters or less';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }

    if (formData.duration === 'custom' && !formData.customDuration) {
      newErrors.customDuration = 'Custom duration is required';
    }

    if (!formData.price && formData.price !== '0') {
      newErrors.price = 'Price is required';
    } else if (isNaN(formData.price) || parseFloat(formData.price) < 0) {
      newErrors.price = 'Price must be a number';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      const finalDuration = formData.duration === 'custom'
        ? formData.customDuration
        : formData.duration;

      const serviceData = {
        name: formData.name,
        tagline: formData.tagline,
        description: formData.description,
        duration: finalDuration,
        price: parseFloat(formData.price),
        serviceType: formData.serviceType,
        location: formData.location,
        showLocationOnWebsite: formData.showLocationOnWebsite,
        active: formData.active,
        serviceImage: formData.serviceImage,
        serviceImagePath: formData.serviceImagePath || null,
        whatToExpect: formData.whatToExpect,
        whoIsThisFor: formData.whoIsThisFor
      };

      onSave(serviceData);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="border border-purple-200 rounded-lg p-5 bg-purple-50/30 space-y-6">
      <h4 className="text-sm font-semibold text-gray-900">
        {isNew ? 'Add Service' : 'Edit Service'}
      </h4>

      {/* SECTION 1: BASIC INFO */}
      <div className="space-y-4">
        <div style={{ fontSize: '11px', fontWeight: 600, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          BASIC INFO
        </div>

        {/* Name */}
        <div>
          <label className="block text-sm font-medium text-gray-900 mb-1">
            Service Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => handleChange('name', e.target.value)}
            placeholder="e.g., Tarot Reading, Reiki"
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          />
          {errors.name && <p className="text-xs text-red-600 mt-1">{errors.name}</p>}
        </div>

        {/* Tagline */}
        <div>
          <label className="block text-sm font-medium text-gray-900 mb-1">
            Tagline <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={formData.tagline}
            onChange={(e) => handleChange('tagline', e.target.value)}
            placeholder="Brief summary"
            maxLength={120}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          />
          <div className="flex items-center justify-between mt-1">
            <p className="text-xs text-gray-500">
              This appears on your main page. Keep it compelling and concise.
            </p>
            <span className="text-xs text-gray-400">{formData.tagline.length}/120</span>
          </div>
          {errors.tagline && <p className="text-xs text-red-600 mt-1">{errors.tagline}</p>}
        </div>
      </div>

      {/* Section Divider */}
      <div style={{ height: '1px', background: '#F3F4F6', margin: '16px 0' }} />

      {/* SECTION 2: FULL DESCRIPTION */}
      <div className="space-y-4">
        <div style={{ fontSize: '11px', fontWeight: 600, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          FULL DESCRIPTION
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-900 mb-1">
            Full Description <span className="text-red-500">*</span>
          </label>
          <SimpleRichEditor
            value={formData.description}
            onChange={(value) => handleChange('description', value)}
            placeholder="Describe this service in detail..."
          />
          <p className="text-xs text-gray-500 mt-1">
            Use the toolbar to format your text. This appears on your service's detail page.
          </p>
          {errors.description && <p className="text-xs text-red-600 mt-1">{errors.description}</p>}
        </div>
      </div>

      {/* Section Divider */}
      <div style={{ height: '1px', background: '#F3F4F6', margin: '16px 0' }} />

      {/* SECTION 3: SERVICE IMAGE */}
      <div className="space-y-4">
        <div style={{ fontSize: '11px', fontWeight: 600, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          SERVICE IMAGE
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-900 mb-2">
            Service Image
          </label>
          <p className="text-xs text-gray-500 mb-3">
            Optional — add an image that represents this service
          </p>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleImageSelect}
            className="hidden"
          />

          {!formData.serviceImage ? (
            <div
              onClick={() => fileInputRef.current?.click()}
              className="border-2 border-dashed border-gray-300 rounded-lg p-6 hover:border-purple-400 transition-colors cursor-pointer"
            >
              <div className="flex flex-col items-center">
                {isUploadingImage ? (
                  <>
                    <Loader2 className="w-8 h-8 text-purple-600 mb-2 animate-spin" />
                    <p className="text-sm text-gray-600">Uploading...</p>
                  </>
                ) : (
                  <>
                    <Upload className="w-8 h-8 text-gray-400 mb-2" />
                    <p className="text-sm font-medium text-gray-700">Upload image</p>
                    <p className="text-xs text-gray-500 mt-1">JPG, PNG, or WEBP • Max 2MB</p>
                  </>
                )}
              </div>
            </div>
          ) : (
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              {/* Image preview - full width */}
              <div className="relative w-full h-32 bg-gray-100">
                <img
                  src={formData.serviceImage}
                  alt="Service image"
                  className="w-full h-full object-cover"
                />
              </div>
              {/* Actions bar */}
              <div className="flex items-center justify-between px-3 py-2 bg-gray-50">
                <span className="text-xs text-gray-500">Image uploaded</span>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="text-xs font-medium text-purple-600 hover:text-purple-700"
                  >
                    Change
                  </button>
                  <button
                    type="button"
                    onClick={handleRemoveImage}
                    className="text-xs font-medium text-red-500 hover:text-red-600"
                  >
                    Remove
                  </button>
                </div>
              </div>
            </div>
          )}

          {uploadError && (
            <div className="mt-2 flex items-center gap-1 text-xs text-red-600">
              <AlertCircle className="w-3 h-3" />
              {uploadError}
            </div>
          )}
        </div>
      </div>

      {/* Section Divider */}
      <div style={{ height: '1px', background: '#F3F4F6', margin: '16px 0' }} />

      {/* SECTION 4: ADDITIONAL DETAILS (Collapsible) */}
      <div className="space-y-4">
        <button
          type="button"
          onClick={() => setShowAdditionalDetails(!showAdditionalDetails)}
          className="flex items-center gap-2 text-sm font-medium text-purple-600 hover:text-purple-700 transition-colors"
        >
          {showAdditionalDetails ? (
            <>
              <ChevronUp className="w-4 h-4" />
              Hide Additional Details
            </>
          ) : (
            <>
              <ChevronDown className="w-4 h-4" />
              Add More Details
            </>
          )}
        </button>

        {showAdditionalDetails && (
          <div className="space-y-4 pt-2">
            <div style={{ fontSize: '11px', fontWeight: 600, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              ADDITIONAL DETAILS
            </div>

            {/* What to Expect */}
            <div>
              <label className="block text-sm font-medium text-gray-900 mb-1">
                What to Expect
              </label>
              <SimpleRichEditor
                value={formData.whatToExpect}
                onChange={(value) => handleChange('whatToExpect', value)}
                placeholder="Walk clients through what happens during a session"
              />
              <p className="text-xs text-gray-500 mt-1">
                Optional — describe the session flow and what clients should prepare for
              </p>
            </div>

            {/* Who Is This For */}
            <div>
              <label className="block text-sm font-medium text-gray-900 mb-1">
                Who Is This For
              </label>
              <SimpleRichEditor
                value={formData.whoIsThisFor}
                onChange={(value) => handleChange('whoIsThisFor', value)}
                placeholder="Describe the ideal client for this service"
              />
              <p className="text-xs text-gray-500 mt-1">
                Optional — help potential clients self-identify if this service is right for them
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Section Divider */}
      <div style={{ height: '1px', background: '#F3F4F6', margin: '16px 0' }} />

      {/* SECTION 5: SESSION DETAILS */}
      <div className="space-y-4">
        <div style={{ fontSize: '11px', fontWeight: 600, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          SESSION DETAILS
        </div>

      {/* Session Type (NEW) */}
      <div>
        <label className="block text-sm font-medium text-gray-900 mb-1">
          Session Type <span className="text-red-500">*</span>
        </label>
        <ToggleGroup
          options={[
            { value: 'virtual', label: 'Virtual' },
            { value: 'in-person', label: 'In-Person' },
            { value: 'hybrid', label: 'Hybrid' }
          ]}
          value={formData.serviceType}
          onChange={(val) => handleChange('serviceType', val)}
        />
      </div>

      {/* Location field - only show for In-Person or Hybrid */}
      {(formData.serviceType === 'in-person' || formData.serviceType === 'hybrid') && (
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-900 mb-1">
              Location
            </label>
            <input
              type="text"
              value={formData.location}
              onChange={(e) => handleChange('location', e.target.value)}
              placeholder="e.g., 123 Main St, Suite 4"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
            <p className="text-xs text-gray-500 mt-1">
              For your reference only. Clients will see this only if you enable display below.
            </p>
          </div>

          <div className="flex items-start gap-2 p-3 bg-gray-50 rounded-lg">
            <Toggle
              enabled={formData.showLocationOnWebsite}
              onChange={(value) => handleChange('showLocationOnWebsite', value)}
            />
            <div className="flex-1">
              <label className="text-sm font-medium text-gray-900 cursor-pointer">
                Show address on website
              </label>
              <p className="text-xs text-gray-600 mt-0.5">
                Keep this OFF to protect your privacy. Clients will request location details after booking.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Duration */}
      <div>
        <label className="block text-sm font-medium text-gray-900 mb-1">
          Duration <span className="text-red-500">*</span>
        </label>
        <select
          value={formData.duration}
          onChange={(e) => handleChange('duration', e.target.value)}
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
        >
          {durationOptions.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>

        {formData.duration === 'custom' && (
          <input
            type="number"
            value={formData.customDuration}
            onChange={(e) => handleChange('customDuration', e.target.value)}
            placeholder="Duration in minutes"
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent mt-2"
          />
        )}
        {errors.customDuration && <p className="text-xs text-red-600 mt-1">{errors.customDuration}</p>}
      </div>

      {/* Price with AI Helper */}
      <div>
        <label className="block text-sm font-medium text-gray-900 mb-1">
          Price (USD) <span className="text-red-500">*</span>
        </label>
        <div className="flex items-center gap-3">
          <div className="relative w-32">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <span className="text-gray-500 text-sm">$</span>
            </div>
            <input
              type="number"
              step="1"
              min="0"
              value={formData.price}
              onChange={(e) => handleChange('price', e.target.value)}
              placeholder="0"
              className="w-full pl-8 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>
          <button
            type="button"
            onClick={() => setShowPricingHelper(!showPricingHelper)}
            className="flex items-center gap-1 text-sm text-[#7c3aed] hover:text-[#6d28d9] font-medium transition-colors"
            style={{ fontSize: '13px' }}
          >
            <Sparkles className="w-4 h-4" />
            Help Me Price This
          </button>
        </div>
        {errors.price && <p className="text-xs text-red-600 mt-1">{errors.price}</p>}

        {/* AI Pricing Helper Panel */}
        {showPricingHelper && (
          <div className="mt-3 bg-purple-50 border border-purple-200 rounded-lg p-4 relative">
            <button
              type="button"
              onClick={() => setShowPricingHelper(false)}
              className="absolute top-2 right-2 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>

            {pricingAnalysis && effectiveDuration ? (
              <>
                <p className="text-sm text-gray-700 mb-2">
                  {formData.name?.trim()
                    ? <>Based on <span className="font-semibold">{pricingAnalysis.category}</span> at {effectiveDuration} minutes, we suggest:</>
                    : <>For a {effectiveDuration}-minute session, we suggest:</>
                  }
                </p>

                <div className="space-y-1.5 mb-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Starting:</span>
                    <button
                      type="button"
                      onClick={() => { handleChange('price', String(pricingAnalysis.prices.low)); setShowPricingHelper(false); }}
                      className="font-semibold text-purple-700 hover:text-purple-800 transition-colors"
                    >
                      ${pricingAnalysis.prices.low}
                    </button>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Average:</span>
                    <button
                      type="button"
                      onClick={() => { handleChange('price', String(pricingAnalysis.prices.mid)); setShowPricingHelper(false); }}
                      className="font-semibold text-purple-700 hover:text-purple-800 transition-colors"
                    >
                      ${pricingAnalysis.prices.mid}
                    </button>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Premium:</span>
                    <button
                      type="button"
                      onClick={() => { handleChange('price', String(pricingAnalysis.prices.high)); setShowPricingHelper(false); }}
                      className="font-semibold text-purple-700 hover:text-purple-800 transition-colors"
                    >
                      ${pricingAnalysis.prices.high}
                    </button>
                  </div>
                </div>

                <p className="text-xs text-gray-600 italic">
                  Based on industry averages for spiritual practitioners
                </p>
              </>
            ) : (
              <p className="text-sm text-gray-600">
                {!effectiveDuration
                  ? 'Select a duration above for pricing suggestions'
                  : 'Enter a service name above for personalized suggestions'}
              </p>
            )}
          </div>
        )}
      </div>
      </div>

      {/* Section Divider */}
      <div style={{ height: '1px', background: '#F3F4F6', margin: '16px 0' }} />

      {/* SECTION 6: VISIBILITY */}
      <div className="space-y-4">
        <div style={{ fontSize: '11px', fontWeight: 600, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          VISIBILITY
        </div>

        <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200">
          <div>
            <p className="text-sm font-medium text-gray-900">Active</p>
            <p className="text-xs text-gray-600">When active, this service appears on your website and clients can book it</p>
          </div>
          <Toggle
            enabled={formData.active}
            onChange={(value) => handleChange('active', value)}
          />
        </div>
      </div>

      {/* Buttons */}
      <div className="flex gap-2 pt-2">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
        >
          Cancel
        </button>
        <button
          type="submit"
          className="flex-1 px-4 py-2 text-sm font-semibold text-white bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors"
        >
          {isNew ? 'Add Service' : 'Save Changes'}
        </button>
      </div>
    </form>
  );
};

// Delete Confirmation Modal
const DeleteConfirmModal = ({ serviceName, onConfirm, onCancel }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div
        className="bg-white rounded-xl shadow-xl"
        style={{ maxWidth: '400px', padding: '24px' }}
      >
        <h3 className="text-gray-900 mb-2" style={{ fontSize: '16px', fontWeight: 600 }}>
          Delete Service?
        </h3>
        <p className="mb-5" style={{ fontSize: '14px', color: '#6B7280' }}>
          Are you sure you want to delete <span className="font-semibold">{serviceName}</span>? This cannot be undone.
        </p>
        <div className="flex items-center justify-end gap-3">
          <button
            onClick={onCancel}
            className="text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors"
            style={{ background: 'none', border: 'none', padding: '8px 12px' }}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="px-6 py-2.5 text-sm font-semibold text-white bg-red-600 hover:bg-red-700 rounded-lg transition-all shadow-sm hover:shadow-md"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
};

export default ServicesPanel;
